import { IPromise, IRootScopeService, IScope } from "angular";
import { IModalServiceInstance, IModalService } from "angular-ui-bootstrap";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { EnvironmentService, IAppConfig } from "@gtmhub/env";
import { LoginService } from "@gtmhub/login/loginService";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IStateInit } from "@gtmhub/core/routing";
import { storage } from "@gtmhub/core/storage";
import { IAccount } from "../../core";

export interface IAccountNameOrDomainScope extends IScope {
  indicators: {
    saving: boolean;
    loading: boolean;
  };
  allowAccountDomainChange: boolean;
  error: {
    accountName: string;
    domain: string;
  };
  accountDomain: {
    protocol: string;
    dc: string;
    toplevelDomain: string;
  };
  emails: {
    send: boolean;
  };
  visualiseAccountNameInput: boolean;
  visualiseDomainInput: boolean;
  isAccountNameAvailable: boolean;
  isLocal: boolean;
  isDomainAvailable: boolean;
  account: IAccount;
  newExperience: { enabled: boolean };
  update(account: { domain?: string; accountName: string }, $uibModalInstance?: IModalServiceInstance): void;
  openConfirmationModal(account: { domain?: string; accountName: string }): void;
  validate(mode: "accountName" | "domain", updatedField: string): void;
  showDomainInput(show: boolean): void;
  showAccountNameInput(show: boolean): void;
  contactSales(): void;
  toggleNewExperience(): void;
  hideDomainInput(): void;
  hideAccountNameInput(): void;
}

export class AccountNameOrDomainChangeCtrl implements IStateInit {
  public static $inject = [
    "appConfig",
    "$scope",
    "$rootScope",
    "$uibModal",
    "AnalyticsService",
    "AccountService",
    "AccountResolverService",
    "EnvironmentService",
    "LoginService",
    "UIErrorHandlingService",
  ];

  constructor(
    appConfig: IAppConfig,
    private $scope: IAccountNameOrDomainScope,
    private $rootScope: IRootScopeService,
    private $uibModal: IModalService,
    private analyticsService: AnalyticsService,
    private accountService: AccountService,
    private accountResolverService: AccountResolverService,
    private env: EnvironmentService,
    private loginService: LoginService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.$scope.validate = this.validate;
    this.$scope.openConfirmationModal = this.openConfirmationModal;
    this.$scope.update = this.update;
    this.$scope.showAccountNameInput = this.showAccountNameInput;
    this.$scope.hideAccountNameInput = this.hideAccountNameInput;
    this.$scope.showDomainInput = this.showDomainInput;
    this.$scope.hideDomainInput = this.hideDomainInput;
    this.$scope.error = {
      accountName: "",
      domain: "",
    };
    this.$scope.visualiseAccountNameInput = false;
    this.$scope.visualiseDomainInput = false;
    this.$scope.contactSales = this.contactSales;
    this.$scope.isLocal = appConfig.env.name === "local";
    this.$scope.indicators = {
      saving: false,
      loading: true,
    };
    this.$scope.account = this.accountResolverService.getAccountData();
    this.$scope.toggleNewExperience = this.toggleNewExperience;
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    const dc = this.env.getDC() === "eu" ? "" : `.${this.env.getDC()}`;
    const envPrefix = this.env.getEnvName() === "staging" ? ".staging" : "";
    this.$scope.accountDomain = {
      protocol: "https://",
      dc: dc,
      toplevelDomain: `${envPrefix}.gtmhub.com`,
    };
    this.$scope.emails = {
      send: false,
    };
    this.$scope.newExperience = this.accountService.getAccountSetting("newExperience") || { enabled: false };

    return this.accountService.checkForSSO().then(
      (result) => {
        this.$scope.allowAccountDomainChange = result.allowAccountDomainChange;
        this.$scope.indicators.loading = false;
      },
      (err) => {
        // no need for displaying error
        // if the query fails we don't want to let the user change its domain
        console.error(err);
        this.$scope.allowAccountDomainChange = false;
        this.$scope.indicators.loading = false;
      }
    );
  };

  private showDomainInput = (): void => {
    this.$scope.visualiseDomainInput = true;
  };

  private hideDomainInput = (): void => {
    this.$scope.visualiseDomainInput = false;
  };

  private showAccountNameInput = (): void => {
    this.$scope.visualiseAccountNameInput = true;
  };

  private hideAccountNameInput = (): void => {
    this.$scope.visualiseAccountNameInput = false;
  };

  private validate = (mode: "accountName" | "domain", updatedField: string) => {
    const regex = /(^[a-zA-Z0-9-]*$)/g;
    const isDomainValid = (mode === "domain" && updatedField?.match(regex)) || mode !== "domain";

    if (!updatedField) {
      this.$scope.error[mode] = "Error message: 'this field cannot be empty'.";

      if (mode === "accountName") {
        this.$scope.isAccountNameAvailable = false;
      } else {
        this.$scope.isDomainAvailable = false;
      }
      return;
    }

    if (!isDomainValid) {
      this.$scope.error.domain = "Error message: 'the domain includes forbidden characters'.";
      this.$scope.isDomainAvailable = false;
      return;
    }

    this.loginService.checkAccountDomain(mode === "accountName" ? { accountName: updatedField } : { domain: updatedField }).then(
      () => {
        if (mode === "accountName") {
          this.$scope.isAccountNameAvailable = true;
        } else {
          this.$scope.isDomainAvailable = true;
        }
      },
      (err) => {
        // instead of showing error here we have check-mark and disabled button
        // to avoid unpleasant ux
        this.$scope.error[mode] = err.data;
        if (mode === "accountName") {
          this.$scope.isAccountNameAvailable = false;
        } else {
          this.$scope.isDomainAvailable = false;
        }
      }
    );
  };

  private update = (futureAccount: { domain?: string; accountName: string }, $uibModalInstance?: IModalServiceInstance): void => {
    this.$scope.indicators.saving = true;
    this.accountService.updateAccount(this.$scope.account.id, futureAccount, this.$scope.emails.send).then(
      () => {
        this.$scope.indicators.saving = false;
        this.accountResolverService.setAccountData(this.$scope.account);
        storage.set("accountName", this.$scope.account.name);

        if (futureAccount.domain) {
          $uibModalInstance.close();
        }
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "saving", inline: true })
    );
  };

  private toggleNewExperience = (): void => {
    this.$scope.newExperience.enabled = !this.$scope.newExperience.enabled;
    this.accountService.setAccountSetting("newExperience", this.$scope.newExperience);
    this.$rootScope.$broadcast("newExperienceSwitch", this.$scope.newExperience.enabled);
  };

  private openConfirmationModal = (account: { domain?: string; accountName: string }) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../views/confirm-account-name-domain-change.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        "$uibModalInstance",
        function ($scope, $uibModalInstance: IModalServiceInstance) {
          $scope.indicators = ctrl.$scope.indicators;
          $scope.cancel = () => {
            $uibModalInstance.close();
          };
          $scope.update = () => {
            ctrl.update(account, $uibModalInstance);
          };
        },
      ],
    });
  };

  private contactSales = (): void => {
    this.analyticsService.contactSales("contactSales");
  };
}
